import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import { GridInitialState } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  getCurrentSettings,
  getCustomViewSubject,
  gridInitialState,
  UNCONFIRMED_WRITE_MESSAGE,
  updateUserCustomizations,
  CustomView,
  UserCustomizations
} from '../../../store/UserCustomizations';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DefaultAssetGridColumnDefs } from '../../Pages/Asset/AssetGridColumnDefs';
import { DefaultLinkGridColumnDefs } from '../../Pages/Link/LinkGridColumnDefs';
import { DefaultCustomerGridColumnDefs } from '../../Pages/Customer/CustomerGridColumnDefs';
import { DefaultRPMGridColumnDefs } from '../../Pages/RPM/RPMGridColumnDefs';
import { DefaultProvisionalAssetGridColumnDefs } from '../../Pages/ProvisionalAsset/ProvisionalAssetGridColumnDefs';
import { DefaultTicketGridColumnDefs } from '../../Pages/Ticket/TicketGridColumnDefs';
import { DefaultTFUGridColumnDefs } from '../../Pages/TFU/TFUGridColumnDefs';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DefaultPaymentGridColumnDefs } from '../../Pages/Payment/PaymentGridColumnDefs';
import { DefaultCorrectionGridColumnDefs } from '../../Pages/Correction/CorrectionGridColumnDefs';
import { DefaultAdjustmentGridColumnDefs } from '../../Pages/Adjustment/AdjustmentGridColumnDefs';
import { DefaultAttributionGridColumnDefs } from '../../Pages/Attribution/DirectSeller/GridColumnDefs';
import { DefaultHistoryGridColumnDefs } from '../../Pages/History/HistoryGridColumnDefs';

export interface ViewsModalParams {
  open: boolean;
  handleClose: () => void;
  type:
    | 'asset'
    | 'RPM'
    | 'link'
    | 'provisionalAsset'
    | 'customer'
    | 'ticket'
    | 'tfu'
    | 'payment'
    | 'correction'
    | 'adjustment'
    | 'attribution'
    | 'history';
}

export interface ViewDisplayItem {
  id: string;
  name: string;
  originalName: string;
  isEdit: boolean;
}

export const ViewsModal = ({ open, handleClose, type }: ViewsModalParams) => {
  const [views, setViews] = useState<ViewDisplayItem[]>([]);
  const [editNameValue, setEditNameValue] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { setSnack } = useSnackbar();

  const sortViews = (v: ViewDisplayItem[]) => {
    return [...v].sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    if (open) {
      const viewSub = getCustomViewSubject().subscribe((allCustomViews: CustomView[]) => {
        const filteredAndSortedViews: ViewDisplayItem[] = sortViews(
          allCustomViews
            .filter((v) => v.page.startsWith(type))
            .map((v) => ({
              id: v.name,
              name: v.name,
              originalName: v.name,
              isEdit: false
            }))
        );
        setViews(filteredAndSortedViews);
        setEditingIndex(null);
        setEditNameValue('');
      });

      return () => {
        if (viewSub) viewSub.unsubscribe();
      };
    }
  }, [open, type]);

  const getGridDefsByType = (viewType: ViewsModalParams['type']): GridInitialState => {
    switch (viewType) {
      case 'asset':
        return gridInitialState(DefaultAssetGridColumnDefs()) as GridInitialState;
      case 'RPM':
        return gridInitialState(DefaultRPMGridColumnDefs) as GridInitialState;
      case 'link':
        return gridInitialState(DefaultLinkGridColumnDefs()) as GridInitialState;
      case 'provisionalAsset':
        return gridInitialState(DefaultProvisionalAssetGridColumnDefs()) as GridInitialState;
      case 'customer':
        return gridInitialState(DefaultCustomerGridColumnDefs()) as GridInitialState;
      case 'ticket':
        return gridInitialState(DefaultTicketGridColumnDefs) as GridInitialState;
      case 'tfu':
        return gridInitialState(DefaultTFUGridColumnDefs()) as GridInitialState;
      case 'payment':
        return gridInitialState(DefaultPaymentGridColumnDefs()) as GridInitialState;
      case 'correction':
        return gridInitialState(DefaultCorrectionGridColumnDefs()) as GridInitialState;
      case 'adjustment':
        return gridInitialState(DefaultAdjustmentGridColumnDefs()) as GridInitialState;
      case 'attribution':
        return gridInitialState(DefaultAttributionGridColumnDefs()) as GridInitialState;
      case 'history':
        return gridInitialState(DefaultHistoryGridColumnDefs()) as GridInitialState;
      default:
        return gridInitialState([]) as GridInitialState;
    }
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditNameValue(views[index].name);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditNameValue('');
  };

  const handleDelete = async (index: number) => {
    setLoading(true);
    const viewToDelete = views[index];
    const viewNameKey = `${type}Grid_${viewToDelete.originalName}`;
    const currentSettings = getCurrentSettings();
    if (!currentSettings) {
      setLoading(false);
      setSnack({ message: 'Failed to retrieve current settings.', type: 'error', open: true });
      return;
    }

    // A copy keeps a rejected delete out of the live document; publishing puts a confirmed one back in.
    const payload: UserCustomizations = JSON.parse(JSON.stringify(currentSettings));
    delete payload.content[viewNameKey as keyof typeof payload.content];
    const { data, error } = await updateUserCustomizations(payload, viewNameKey, true);
    if (data) {
      setSnack({ message: 'Your view was deleted successfully', type: 'success', open: true });
    } else {
      setSnack({
        message: `There was an error deleting your view: \n${error?.message ?? UNCONFIRMED_WRITE_MESSAGE}`,
        type: 'error',
        open: true
      });
    }
    setLoading(false);
  };

  const handleSave = async (index: number) => {
    const originalViewItem = views[index];
    const newName = editNameValue.trim();
    if (!newName) {
      handleCancelEdit();
      return;
    }

    if (newName === originalViewItem.originalName) {
      handleCancelEdit();
      return;
    }

    setLoading(true);
    const currentSettings = getCurrentSettings();
    if (!currentSettings) {
      setLoading(false);
      setSnack({ message: 'Failed to retrieve current settings.', type: 'error', open: true });
      return;
    }
    const oldPrefKey = `${type}Grid_${originalViewItem.originalName}` as keyof typeof currentSettings.content;
    const newPrefKey = `${type}Grid_${newName}` as keyof typeof currentSettings.content;
    const currentViewData = currentSettings.content[oldPrefKey];
    // A copy keeps a rejected rename out of the live document; publishing puts a confirmed one back in.
    const payload: UserCustomizations = JSON.parse(JSON.stringify(currentSettings));
    delete payload.content[oldPrefKey];
    payload.content[newPrefKey] = {
      ...currentViewData,
      muiConfig: currentViewData?.muiConfig || getGridDefsByType(type),
      disabledFields: currentViewData?.disabledFields || []
    };
    const { data, error } = await updateUserCustomizations(payload, newPrefKey, true);
    if (data) {
      setSnack({ message: 'Your view was updated successfully', type: 'success', open: true });
      setEditingIndex(null);
      setEditNameValue('');
    } else {
      setSnack({
        message: `There was an error updating your view: \n${error?.message ?? UNCONFIRMED_WRITE_MESSAGE}`,
        type: 'error',
        open: true
      });
      setEditingIndex(index);
    }
    setLoading(false);
  };

  return (
    <>
      <Dialog
        data-testid="views-modal"
        open={open}
        onClose={handleClose}
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', paddingBottom: '1.7rem' }}>
          <TextBlock
            color="grey9"
            size="lg">
            Edit Views
          </TextBlock>
        </DialogTitle>
        <DialogContent
          sx={{ height: 300, overflow: 'auto', paddingTop: 'unset', paddingRight: '.7rem', paddingLeft: '.7rem' }}>
          {loading ? (
            <div className="mt-[5rem] items-center self-center">
              <ComponentSpinner label="Saving..." />
            </div>
          ) : (
            <>
              {!!views.length && (
                <div className="flex border-b border-t mx-4 items-center">
                  <TextBlock
                    color="grey6"
                    size="sm">
                    View
                  </TextBlock>
                  <div className="ml-auto space-x-0 flex items-center border-l py-2 text-grey-6 w-16 pl-2 text-sm">
                    Actions
                  </div>
                </div>
              )}
              {views.map((view: ViewDisplayItem, index: number) => (
                <div
                  className="flex border-b mx-4 items-center"
                  key={view.id}>
                  {editingIndex === index ? (
                    <div className="flex grow space-x-3 items-center">
                      <TextField
                        required
                        id={`name-${view.id}`}
                        name="name"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSave(index);
                          }
                        }}
                        fullWidth
                        sx={{ my: 0 }}
                        slotProps={{
                          htmlInput: {
                            autoFocus: true,
                            className: '!py-2 !text-sm'
                          }
                        }}
                      />
                      <div className="ml-auto space-x-0 flex items-center border-l py-2 w-16">
                        <IconButton
                          sx={{
                            color: 'rgb(107 114 128)'
                          }}
                          size="small"
                          onClick={() => handleSave(index)}>
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          sx={{
                            color: 'rgb(107 114 128)'
                          }}
                          size="small"
                          onClick={handleCancelEdit}>
                          <CancelIcon />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <>
                      <TextBlock
                        color="grey9"
                        size="sm">
                        {view.name}
                      </TextBlock>
                      <div className="ml-auto space-x-0 flex items-center border-l py-2 w-16">
                        <IconButton
                          sx={{
                            color: 'rgb(107 114 128)'
                          }}
                          size="small"
                          onClick={() => handleEditClick(index)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          sx={{
                            color: 'rgb(107 114 128)'
                          }}
                          size="small"
                          onClick={() => handleDelete(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </>
          )}
          {!views.length && !loading && (
            <div className="flex w-full justify-center mt-[5rem]">
              <TextSpan>No Views</TextSpan>
            </div>
          )}
        </DialogContent>
        <hr className="mb-1" />
        <DialogActions sx={{ marginRight: 2, pb: 2 }}>
          <button
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={handleClose}>
            Close
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};
